import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import PartnersLoggedIn from "./partners-logged-in";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
} from "@mui/material";
import { fetchPartnerData } from "../../redux/thunks/thunk-partners";
import { AppDispatch, RootState } from "../../redux/store";
import Reports from "./reports";
import ComingSoon from "../../components/coming-soon";

// Update your Cognito User Pool details.
const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};

const userPool = new CognitoUserPool(poolData);

export default function Partners() {
  const dispatch = useDispatch<AppDispatch>();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthorizedPartner, setIsAuthorizedPartner] =
    useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>("");
  // Updated selectedItem values: "clients", "reports", "workoutTemplates", "myProfile", "settings"
  const [selectedItem, setSelectedItem] = useState<string>("clients");
  // State to track if the drawer is expanded (open) or collapsed.

  const isLoading =
    useSelector((state: RootState) => state.partners.partnerStatus) ==
    "loading";
  const darkMode = useSelector((state: RootState) => state.partners.darkMode);

  // Check if a user is already logged in.
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (session && session.isValid()) {
          currentUser.getUserAttributes(
            (err: any, attributes?: CognitoUserAttribute[]) => {
              if (err) {
                setError(err.message || JSON.stringify(err));
                return;
              }
              if (attributes) {
                const emailAttr = attributes.find(
                  (attr: CognitoUserAttribute) => attr.getName() === "email"
                );
                const subAttr = attributes.find(
                  (attr: CognitoUserAttribute) => attr.getName() === "sub"
                );
                const email = emailAttr ? emailAttr.getValue() : "";
                const userId = subAttr ? subAttr.getValue() : "";
                setUserEmail(email);
                setIsAuthenticated(true);
                const isPartner = true;
                setIsAuthorizedPartner(isPartner);
                if (isPartner) {
                  const idToken = session.getIdToken().getJwtToken();
                  // Dispatch the thunk with both idToken and userId.
                  const userId = subAttr ? subAttr.getValue() : "";
                  dispatch(fetchPartnerData({ idToken, userId }));
                }
              }
            }
          );
          const idToken = session.getIdToken().getJwtToken();
        }
      });
    }
  }, [dispatch]);

  // Handle login form submission.
  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const userData = {
      Username: username,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);
    cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        cognitoUser.getUserAttributes(
          (err: any, attributes?: CognitoUserAttribute[]) => {
            if (err) {
              setError(err.message || JSON.stringify(err));
              return;
            }

            if (!attributes) {
              setError("No attributes found");
              return;
            }

            const emailAttr = attributes.find(
              (attr: CognitoUserAttribute) => attr.getName() === "email"
            );
            const subAttr = attributes.find(
              (attr: CognitoUserAttribute) => attr.getName() === "sub"
            );
            const email = emailAttr ? emailAttr.getValue() : "";
            const userId = subAttr ? subAttr.getValue() : "";
            setUserEmail(email);
            setIsAuthenticated(true);
            const isPartner = true;
            setIsAuthorizedPartner(isPartner);
            if (isPartner) {
              const idToken = session.getIdToken().getJwtToken();
              // Dispatch the thunk with both idToken and userId.
              dispatch(fetchPartnerData({ idToken, userId }));
            }
          }
        );
      },
      onFailure: (err: any) => {
        setError(err.message || JSON.stringify(err));
      },
    });
  };

  const handleLogout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    // Reset state.
    setIsAuthenticated(false);
    setIsAuthorizedPartner(false);
    setUsername("");
    setPassword("");
    setUserEmail("");
  };

  // Render the login form if not authenticated.
  if (!isAuthenticated) {
    return (
      <Container
        maxWidth="sm"
        style={{
          backgroundColor: "white",
          justifyContent: "center",
          height: "100vh",
          alignContent: "center",
        }}
      >
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Partner Log in
          </Typography>
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              columns: 5,
            }}
          >
            <TextField
              label="Username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" variant="contained" color="primary">
              Login
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Container>
    );
  }

  // If authenticated but not an authorized partner.
  if (!isAuthorizedPartner) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ padding: 3, marginTop: 8 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Unauthorized
          </Typography>
          <Typography variant="body1" align="center" sx={{ marginBottom: 2 }}>
            Sorry, you are not an authorized partner.
          </Typography>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Render the partner view.
  return isLoading ? (
    <Box
      sx={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
      }}
    >
      {/* 🔄 Fullscreen looping background video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          objectFit: "cover",
          zIndex: 9,
        }}
      >
        <source src="/images/background-loading-media.mp4" type="video/mp4" />
      </video>
      {/* ✅ Blur overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 9,
          backdropFilter: "blur(128px)",
          WebkitBackdropFilter: "blur(128px)",
          background: "rgba(0,0,0,0.25)", // optional slight tint
        }}
      />

      {/* 🔤 Metallic gradient text overlay */}
      <Box
        sx={{
          textAlign: "center",
          zIndex: 10,
          userSelect: "none",
          px: 2,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 900,
            letterSpacing: "0.05em",
            background: `linear-gradient(90deg, #c0c0c0, #e5e5e5, #a8a8a8, #c0c0c0)`,
            backgroundSize: "300%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "gradientShift 5s ease-in-out infinite",
            textTransform: "uppercase",
          }}
        >
          AVRO FITNESS
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            color: "rgba(255,255,255,0.85)",
            fontWeight: 400,
            letterSpacing: "0.05em",
          }}
        >
          Setting up your workspace…
        </Typography>
      </Box>

      {/* 🔁 Keyframes for metallic gradient animation */}
      <style>{`
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </Box>
  ) : (
    <>
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          display: "flex",
          minHeight: "100vh",
          position: "fixed",
          background: `
      radial-gradient(
        circle at 0% 0%,
        ${darkMode ? "#0F1117" : "white"} 0%,
        ${darkMode ? "#0F1117" : "white"} 100%
      )
    `,
          backgroundBlendMode: "overlay",
          animation: "moveGradient 20s ease-in-out infinite alternate",
          "@keyframes moveGradient": {
            "0%": { backgroundPosition: "100% 100%" },
            "50%": { backgroundPosition: "60% 40%" },
            "100%": { backgroundPosition: "100% 100%" },
          },
          backgroundSize: "150% 150%", // optional for more dramatic movement
        }}
      >
        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            position: "relative",
            zIndex: 100,
            flexGrow: 1,
            mt: 0,
            overflow: "hidden",

            // Create the background image as a pseudo-element
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: `url(/images/${darkMode ? "" : "partner-bg"}.png)`,
              backgroundColor: darkMode ? "#0F1117" : "",
              filter: "blur(480px)", // ← adjust this value for more/less blur
              transform: "scale(1.05)", // prevents edge clipping when blurred
              zIndex: -1,
            },
          }}
        >
          {selectedItem === "clients" && <PartnersLoggedIn />}
          {selectedItem === "reports" && <Reports />}
          {selectedItem === "workoutTemplates" && (
            <Box>
              {/* <CreateTemplate /> */}
              <ComingSoon />
            </Box>
          )}
          {selectedItem === "myProfile" && (
            <Box>
              {/* <Typography variant="h5">My Profile Placeholder</Typography> */}
              <ComingSoon />
            </Box>
          )}
          {selectedItem === "settings" && (
            <Box>
              {/* <Typography variant="h5">Settings Placeholder</Typography> */}
              <ComingSoon />
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
}
